/**
 * 拣选策略配置合并表单 Drawer 组件
 * 包含：称重商品上下限、拣选任务完成时间上限、App 端排序策略 三个配置
 */

import React, { useEffect, useState } from 'react';
import {
  Drawer, Form, InputNumber, Select, Button, Space, Divider,
  message, Modal, Typography, Row, Col,
} from 'antd';
import { DeleteOutlined, ExclamationCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type {
  PickingStrategyConfigDTO,
  PickingTaskSortRule,
} from '../../../types/picking-strategy/pickingStrategy';
import * as pickingStrategyService from '../../../services/picking-strategy/pickingStrategyService';

interface PickingStrategyFormProps {
  visible: boolean;
  mode: 'create' | 'edit';
  record: PickingStrategyConfigDTO | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface FormValues {
  upperLimitPercent: number | undefined;
  lowerLimitPercent: number | undefined;
  maxCompletionMinutes: number | undefined;
  sortRules: PickingTaskSortRule[];
}

const PickingStrategyForm: React.FC<PickingStrategyFormProps> = ({
  visible,
  mode,
  record,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<FormValues>();
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  // 排序字段选项
  const SORT_ITEM_OPTIONS = [
    { value: 'ORDER_CREATE_TIME', label: t('pickingStrategy.sortPolicy.sortItems.orderCreateTime') },
    { value: 'ORDER_NO', label: t('pickingStrategy.sortPolicy.sortItems.orderNo') },
  ];

  const SORT_TYPE_OPTIONS = [
    { value: 'ASC', label: t('pickingStrategy.sortPolicy.sortTypes.ascending') },
    { value: 'DESC', label: t('pickingStrategy.sortPolicy.sortTypes.descending') },
  ];

  // 打开时回填数据
  useEffect(() => {
    if (!visible) return;
    if (record) {
      // 使用 setTimeout 确保 Drawer 动画完成、Form 挂载后再回填
      setTimeout(() => {
        form.setFieldsValue({
          upperLimitPercent: record.upperLimitPercent ?? undefined,
          lowerLimitPercent: record.lowerLimitPercent ?? undefined,
          maxCompletionMinutes: record.maxCompletionMinutes ?? undefined,
          sortRules: record.sortRules ?? [],
        });
      }, 0);
    } else if (mode === 'create') {
      form.resetFields();
    }
    setIsDirty(false);
  }, [visible, record, mode, form]);

  // 关闭前确认
  const handleClose = () => {
    if (isDirty) {
      Modal.confirm({
        title: t('common.confirm'),
        icon: <ExclamationCircleOutlined />,
        content: t('common.saveConfirm.message'),
        onOk: () => {
          form.resetFields();
          setIsDirty(false);
          onClose();
        },
      });
    } else {
      form.resetFields();
      onClose();
    }
  };

  // 提交保存
  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      setSaving(true);
      const response = await pickingStrategyService.savePickingStrategyConfig({
        upperLimitPercent: values.upperLimitPercent,
        lowerLimitPercent: values.lowerLimitPercent,
        maxCompletionMinutes: values.maxCompletionMinutes,
        sortRules: (values.sortRules ?? []).map((rule, idx) => ({
          ...rule,
          priority: idx + 1,
        })),
      });

      if (!response.success) {
        message.error(response.errorMessage || t('common.message.operationFailed'));
        return;
      }

      message.success(
        mode === 'create'
          ? t('pickingStrategy.form.message.createSuccess')
          : t('pickingStrategy.form.message.saveSuccess')
      );
      setIsDirty(false);
      form.resetFields();
      onSuccess();
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'errorFields' in error) {
        return;
      }
      message.error(t('common.message.operationFailed'));
    } finally {
      setSaving(false);
    }
  };

  const drawerTitle =
    mode === 'create'
      ? t('pickingStrategy.form.title.create')
      : t('pickingStrategy.form.title.edit');

  return (
    <Drawer
      title={drawerTitle}
      placement="right"
      width={720}
      open={visible}
      onClose={handleClose}
      destroyOnClose
      footer={
        <div style={{ textAlign: 'right' }}>
          <Space>
            <Button onClick={handleClose}>{t('common.cancel')}</Button>
            <Button type="primary" loading={saving} onClick={handleSave}>
              {t('common.save')}
            </Button>
          </Space>
        </div>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onValuesChange={() => setIsDirty(true)}
      >
        {/* 称重商品上下限 */}
        <Divider orientation="left">
          {t('pickingStrategy.weightedItemLimit.sectionTitle')}
        </Divider>
        {/* 功能介绍 */}
        <div style={{
          background: '#e6f4ff',
          borderRadius: 8,
          padding: '12px 16px',
          marginBottom: 16,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 8
        }}>
          <ExclamationCircleOutlined style={{ color: '#1677ff', marginTop: 2 }} />
          <span style={{ color: '#0958d9', fontSize: 14 }}>
            {t('pickingStrategy.weightedItemLimit.description')}
          </span>
        </div>
        <Form.Item
          name="upperLimitPercent"
          label={t('pickingStrategy.weightedItemLimit.form.upperLimitPercent')}
          rules={[
            {
              type: 'number',
              min: 0,
              max: 100,
              message: t('pickingStrategy.weightedItemLimit.form.upperLimitPercentRange'),
            },
          ]}
        >
          <InputNumber
            min={0}
            max={100}
            precision={0}
            addonAfter="%"
            style={{ width: '100%' }}
            placeholder={t('pickingStrategy.weightedItemLimit.form.upperLimitPercentPlaceholder')}
          />
        </Form.Item>
        <Form.Item
          name="lowerLimitPercent"
          label={t('pickingStrategy.weightedItemLimit.form.lowerLimitPercent')}
          rules={[
            {
              type: 'number',
              min: 0,
              max: 100,
              message: t('pickingStrategy.weightedItemLimit.form.lowerLimitPercentRange'),
            },
          ]}
        >
          <InputNumber
            min={0}
            max={100}
            precision={0}
            addonAfter="%"
            style={{ width: '100%' }}
            placeholder={t('pickingStrategy.weightedItemLimit.form.lowerLimitPercentPlaceholder')}
          />
        </Form.Item>

        {/* 拣选任务完成时间上限 */}
        <Divider orientation="left">
          {t('pickingStrategy.taskCompletionTimeLimit.sectionTitle')}
        </Divider>
        {/* 功能介绍 */}
        <div style={{
          background: '#e6f4ff',
          borderRadius: 8,
          padding: '12px 16px',
          marginBottom: 16,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 8
        }}>
          <ExclamationCircleOutlined style={{ color: '#1677ff', marginTop: 2 }} />
          <span style={{ color: '#0958d9', fontSize: 14 }}>
            {t('pickingStrategy.taskCompletionTimeLimit.description')}
          </span>
        </div>
        <Form.Item
          name="maxCompletionMinutes"
          label={t('pickingStrategy.taskCompletionTimeLimit.form.maxCompletionMinutes')}
          rules={[
            {
              type: 'number',
              min: 1,
              max: 9999,
              message: t('pickingStrategy.taskCompletionTimeLimit.form.maxCompletionMinutesRange'),
            },
          ]}
        >
          <InputNumber
            min={1}
            max={9999}
            precision={0}
            addonAfter={t('pickingStrategy.taskCompletionTimeLimit.form.maxCompletionMinutesUnit')}
            style={{ width: '100%' }}
            placeholder={t('pickingStrategy.taskCompletionTimeLimit.form.maxCompletionMinutesPlaceholder')}
          />
        </Form.Item>

        {/* App 端拣选任务排序策略 */}
        <Divider orientation="left">
          {t('pickingStrategy.sortPolicy.sectionTitle')}
        </Divider>
        {/* 功能介绍 */}
        <div style={{
          background: '#e6f4ff',
          borderRadius: 8,
          padding: '12px 16px',
          marginBottom: 16,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 8
        }}>
          <ExclamationCircleOutlined style={{ color: '#1677ff', marginTop: 2 }} />
          <span style={{ color: '#0958d9', fontSize: 14 }}>
            {t('pickingStrategy.sortPolicy.description')}
          </span>
        </div>

        <Form.List name="sortRules">
          {(fields, { add, remove }) => (
            <>
              {/* 表格标题行 */}
              <div style={{ marginBottom: 8, color: '#666', fontSize: 13 }}>
                {t('pickingStrategy.sortPolicy.tableTitle')}&nbsp;
                <span style={{ color: '#1677ff' }}>
                  {t('pickingStrategy.sortPolicy.totalItems', { count: fields.length })}
                </span>
              </div>

              {/* 表头 */}
              {fields.length > 0 && (
                <Row
                  gutter={8}
                  style={{
                    background: '#fafafa',
                    padding: '8px 4px',
                    fontWeight: 600,
                    fontSize: 13,
                    borderBottom: '1px solid #f0f0f0',
                    marginBottom: 0,
                  }}
                >
                  <Col span={9}>
                    <span style={{ color: '#ff4d4f' }}>* </span>
                    {t('pickingStrategy.sortPolicy.column.sortItem')}
                  </Col>
                  <Col span={8}>
                    <span style={{ color: '#ff4d4f' }}>* </span>
                    {t('pickingStrategy.sortPolicy.column.sortType')}
                  </Col>
                  <Col span={4}>
                    <span style={{ color: '#ff4d4f' }}>* </span>
                    {t('pickingStrategy.sortPolicy.column.priority')}
                  </Col>
                  <Col span={3}>
                    {t('pickingStrategy.sortPolicy.column.actions')}
                  </Col>
                </Row>
              )}

              {/* 数据行 */}
              {fields.map((field, index) => (
                <Row
                  key={field.key}
                  gutter={8}
                  align="middle"
                  style={{
                    padding: '8px 4px',
                    borderBottom: '1px solid #f0f0f0',
                  }}
                >
                  <Col span={9}>
                    <Form.Item
                      name={[field.name, 'sortItem']}
                      noStyle
                      rules={[{ required: true, message: t('pickingStrategy.sortPolicy.validation.sortItemRequired') }]}
                    >
                      <Select
                        placeholder={t('pickingStrategy.sortPolicy.column.sortItem')}
                        options={SORT_ITEM_OPTIONS}
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name={[field.name, 'sortType']}
                      noStyle
                      rules={[{ required: true, message: t('pickingStrategy.sortPolicy.validation.sortTypeRequired') }]}
                    >
                      <Select
                        placeholder={t('pickingStrategy.sortPolicy.column.sortType')}
                        options={SORT_TYPE_OPTIONS}
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={4} style={{ textAlign: 'center' }}>
                    {index + 1}
                  </Col>
                  <Col span={3}>
                    <Button
                      type="link"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => remove(field.name)}
                      style={{ padding: 0 }}
                    >
                      {t('pickingStrategy.sortPolicy.delete')}
                    </Button>
                  </Col>
                </Row>
              ))}

              {/* 添加行按钮 */}
              <Button
                block
                icon={<PlusOutlined />}
                onClick={() => add({ sortType: 'DESC' })}
                style={{ marginTop: 8, borderStyle: 'dashed' }}
              >
                {t('pickingStrategy.sortPolicy.add')}
              </Button>
            </>
          )}
        </Form.List>
      </Form>
    </Drawer>
  );
};

export default PickingStrategyForm;
